import { useState, useEffect, useMemo } from "react";
import { useTheme } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";

/**
 * Hook personalizado para gestionar el diseño responsive del módulo dental
 * Adapta el layout, tamaños y interacciones según el dispositivo
 */
export default function useDentalResponsive() {
  const theme = useTheme();
  const [orientation, setOrientation] = useState(
    window.innerHeight > window.innerWidth ? "portrait" : "landscape"
  );

  // Breakpoints
  const isXS = useMediaQuery(theme.breakpoints.only("xs")); // 0-599px
  const isSM = useMediaQuery(theme.breakpoints.only("sm")); // 600-959px
  const isMD = useMediaQuery(theme.breakpoints.only("md")); // 960-1279px
  const isLG = useMediaQuery(theme.breakpoints.only("lg")); // 1280-1919px
  const isXL = useMediaQuery(theme.breakpoints.up("xl")); // 1920px+

  // Tamaños específicos
  const isMobile = useMediaQuery(theme.breakpoints.down("sm")); // <= 959px
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "lg")); // 600-1279px
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg")); // >= 1280px

  // Detectar orientación
  useEffect(() => {
    const handleOrientationChange = () => {
      setOrientation(
        window.innerHeight > window.innerWidth ? "portrait" : "landscape"
      );
    };

    window.addEventListener("resize", handleOrientationChange);
    window.addEventListener("orientationchange", handleOrientationChange);

    return () => {
      window.removeEventListener("resize", handleOrientationChange);
      window.removeEventListener("orientationchange", handleOrientationChange);
    };
  }, []);

  // Configuración del layout por dispositivo
  const layoutConfig = useMemo(() => {
    if (isMobile) {
      return {
        deviceType: "mobile",
        toothSize: orientation === "portrait" ? 32 : 28,
        quadrantLayout: "vertical", // stack quadrants vertically
        statsLayout: "compact",
        showPerformanceByDefault: false,
        chartSpacing: 0.25,
        fontScale: 0.9,
        maxWidth: "100%",
        padding: 0.25,
        itemsPerRow: orientation === "portrait" ? 4 : 6,
        showQuadrantTitles: true,
        compactMode: true,
      };
    }

    if (isTablet) {
      return {
        deviceType: "tablet",
        toothSize: orientation === "portrait" ? 36 : 38,
        quadrantLayout: "grid",
        statsLayout: "expanded",
        showPerformanceByDefault: false,
        chartSpacing: 0.5,
        fontScale: 1,
        maxWidth: "100%",
        padding: 0.5,
        itemsPerRow: orientation === "portrait" ? 6 : 8,
        showQuadrantTitles: true,
        compactMode: false,
      };
    }

    return {
      deviceType: "desktop",
      toothSize: 42,
      quadrantLayout: "grid",
      statsLayout: "full",
      showPerformanceByDefault: true,
      chartSpacing: 0.5,
      fontScale: 1,
      maxWidth: "100%",
      padding: 0.75,
      itemsPerRow: 8,
      showQuadrantTitles: true,
      compactMode: false,
    };
  }, [isMobile, isTablet, orientation]);

  // Configuración específica para cuadrantes
  const getQuadrantConfig = useMemo(
    () => (quadrantName) => {
      const baseConfig = {
        minHeight: isMobile ? "90px" : "100px",
        padding: isMobile ? theme.spacing(0.5) : theme.spacing(0.75),
        borderRadius: theme.spacing(0.5),
      };

      if (layoutConfig.quadrantLayout === "vertical") {
        return {
          ...baseConfig,
          width: "100%",
          marginBottom: theme.spacing(0.5),
          gridColumns: Math.min(layoutConfig.itemsPerRow, 8),
        };
      }

      return {
        ...baseConfig,
        width: "100%",
        gridColumns: Math.min(layoutConfig.itemsPerRow, 8),
      };
    },
    [layoutConfig, isMobile, theme]
  );

  // Configuración para dientes individuales
  const getToothConfig = useMemo(
    () => ({
      size: layoutConfig.toothSize,
      spacing: layoutConfig.chartSpacing,
      interactive: !isMobile, // En móvil, puede ser menos preciso el toque
      showTooltips: !isMobile, // Tooltips pueden interferir en móvil
      hoverEffects: !isMobile,
      touchOptimized: isMobile,
      minTapTarget: isMobile ? 44 : 32, // Accessibility guideline
    }),
    [layoutConfig, isMobile]
  );

  // Configuración para estadísticas
  const getStatsConfig = useMemo(() => {
    if (layoutConfig.statsLayout === "compact") {
      return {
        direction: "column",
        chipSize: "small",
        showPerformance: false,
        maxChipsPerRow: 2,
        spacing: 0.5,
      };
    }

    if (layoutConfig.statsLayout === "expanded") {
      return {
        direction: "row",
        chipSize: "small",
        showPerformance: true,
        maxChipsPerRow: 3,
        spacing: 1,
      };
    }

    return {
      direction: "row",
      chipSize: "medium",
      showPerformance: true,
      maxChipsPerRow: 5,
      spacing: 1,
    };
  }, [layoutConfig.statsLayout]);

  // Estilos responsivos para Grid
  const getGridProps = useMemo(() => {
    if (layoutConfig.quadrantLayout === "vertical") {
      return {
        container: true,
        spacing: 0.5,
        direction: "column",
        xs: 12,
        sm: 12,
        md: 12,
        lg: 12,
      };
    }

    return {
      spacing: 0.5,
      xs: 12,
      sm: 6,
      md: 6,
      lg: 6,
    };
  }, [layoutConfig]);

  // Configuración de modales y diálogos
  const getModalConfig = useMemo(
    () => ({
      fullScreen: isMobile,
      maxWidth: isMobile ? false : isTablet ? "md" : "lg",
      fullWidth: true,
      scroll: isMobile ? "body" : "paper",
      disableBackdropClick: isMobile,
    }),
    [isMobile, isTablet]
  );

  // Configuración de menús
  const getMenuConfig = useMemo(
    () => ({
      anchorOrigin: isMobile
        ? { vertical: "bottom", horizontal: "center" }
        : { vertical: "bottom", horizontal: "left" },
      transformOrigin: isMobile
        ? { vertical: "top", horizontal: "center" }
        : { vertical: "top", horizontal: "left" },
      getContentAnchorEl: null, // Para evitar warning en móvil
      disableScrollLock: isMobile,
    }),
    [isMobile]
  );

  // Utilidades de touch para móvil
  const getTouchHandlers = useMemo(() => {
    if (!isMobile) return {};

    return {
      onTouchStart: (e) => {
        // Prevenir zoom accidental
        if (e.touches.length > 1) {
          e.preventDefault();
        }
      },
      onTouchMove: (e) => {
        // Permitir scroll vertical, prevenir horizontal
        const touch = e.touches[0];
        const startY = touch.clientY;
        const threshold = 10;

        if (Math.abs(touch.clientY - startY) < threshold) {
          e.preventDefault();
        }
      },
    };
  }, [isMobile]);

  // Configuración de renderizado condicional
  const shouldRender = useMemo(
    () => ({
      quadrantTitles: layoutConfig.showQuadrantTitles,
      performanceInfo: layoutConfig.showPerformanceByDefault,
      detailedStats: !layoutConfig.compactMode,
      tooltips: !isMobile,
      hoverEffects: !isMobile,
      keyboardShortcuts: !isMobile,
      dragAndDrop: isDesktop,
      advancedFilters: !isMobile,
    }),
    [layoutConfig, isMobile, isDesktop]
  );

  // Configuración de spacing y dimensiones
  const getDimensions = useMemo(
    () => ({
      chartMaxWidth: layoutConfig.maxWidth,
      chartPadding: theme.spacing(layoutConfig.padding),
      quadrantSpacing: theme.spacing(layoutConfig.chartSpacing),
      toothSpacing: theme.spacing(layoutConfig.chartSpacing * 0.5),
      borderRadius: theme.spacing(1),
      fontScale: layoutConfig.fontScale,
    }),
    [layoutConfig, theme]
  );

  // Configuración de animaciones
  const getAnimationConfig = useMemo(
    () => ({
      enabled: !isMobile, // Reducir animaciones en móvil para mejor performance
      duration: isMobile ? 200 : 300,
      easing: theme.transitions.easing.easeInOut,
      staggerDelay: isMobile ? 25 : 50,
    }),
    [isMobile, theme]
  );

  return {
    // Device detection
    deviceInfo: {
      isMobile,
      isTablet,
      isDesktop,
      isXS,
      isSM,
      isMD,
      isLG,
      isXL,
      orientation,
      deviceType: layoutConfig.deviceType,
    },

    // Layout configuration
    layoutConfig,

    // Component-specific configs
    getQuadrantConfig,
    getToothConfig,
    getStatsConfig,
    getGridProps,
    getModalConfig,
    getMenuConfig,
    getDimensions,
    getAnimationConfig,

    // Interaction configs
    getTouchHandlers,
    shouldRender,

    // Utility functions
    isCompactMode: layoutConfig.compactMode,
    getResponsiveSpacing: (base) =>
      theme.spacing(base * layoutConfig.fontScale),
    getResponsiveFontSize: (base) => `${base * layoutConfig.fontScale}rem`,
  };
}
